import { ErrorType } from "@/composable/application/errors";

/**
 * Extrait le code metier ( ex {@code "EXISTING_LOGIN"} ,
 * {@code "AUTHORIZATION_NAME_EXISTS"} , {@code "BAD_VALIDATION_KEY"} , ...)
 * du body de la reponse d'erreur backend . Le body est une
 * {@code Promise<unknown>} portee par {@code ErrorType.content} - le backend
 * Spring serialise les exceptions selon plusieurs formes ( legacy
 * incoherent ) :
 * <ul>
 *   <li>String brute ( ex Spring renvoie {@code body(eee.getMessage())} ou
 *       {@code body("AUTHORIZATION_NAME_EXISTS")} )</li>
 *   <li>Map avec champ {@code code} ( ex
 *       {@code Map.of("code", "AUTHORIZATION_NAME_EXISTS", ...)} )</li>
 *   <li>Map avec champ {@code message} contenant le code ( pattern
 *       AuthenticationFailure )</li>
 * </ul>
 *
 * <p>Retourne {@code ""} si le code ne peut etre extrait ( body
 * {@code null} , Promise rejected , objet d'une autre forme ) . Garde-fou
 * critique : un code vide ne doit jamais matcher un check
 * {@code === "SOME_CODE"} cote caller , qui basculerait alors sur le
 * toast generique , ce qui est preferable a un faux positif sur un code
 * particulier .
 *
 * <p>Pure : pas de side effect , pas de dependance i18n / services .
 * Testable unitairement sans mock .
 */
export async function extractServerErrorCode(err: ErrorType | undefined | null): Promise<string> {
  if (err?.content == null) return "";
  try {
    const body = await err.content;
    if (typeof body === "string") return body;
    if (typeof body === "object" && body !== null && !Array.isArray(body)) {
      // Double cast via {@code unknown} : {@code ErrorType.content} est type
      // {@code Promise<ValidationCheckResultRest[] | DefaultValidationCheckResult[]>}
      // dans la signature historique , mais en realite les endpoints renvoient
      // aussi des Map ( ex {@code Map.of("code", "...")} ) . TS refuse le cast
      // direct array -> Record . On force via {@code unknown} apres avoir
      // exclu les arrays via {@code Array.isArray} .
      const record = body as unknown as Record<string, unknown>;
      if (typeof record.code === "string") return record.code;
      if (typeof record.message === "string") return record.message;
    }
    return "";
  } catch {
    return "";
  }
}
